(() => {
  'use strict';
  const root = document.querySelector('[data-hf-cookie-consent]');
  if (!root || root.dataset.initialized) return;
  root.dataset.initialized = 'true';
  const banner = root.querySelector('[data-hf-cookie-banner]');
  const dialog = root.querySelector('[data-hf-cookie-dialog]');
  const announcement = root.querySelector('[data-hf-cookie-announcement]');
  const purposes = ['preferences', 'analytics', 'marketing'];
  let loading = null;
  let saving = false;
  let dismissed = false;
  let returnFocus = null;
  let autoTimer;

  // Shopify documents window.privacyBanner. Also support a namespaced instance
  // if Shopify has already supplied one; never create or impersonate it.
  function nativeBanner() {
    const native = window.privacyBanner || window.Shopify?.privacyBanner;
    return typeof native?.showPreferences === 'function' ? native : null;
  }
  function api() { return window.Shopify?.customerPrivacy; }
  function showError(message) {
    root.querySelectorAll('[data-hf-cookie-error]').forEach(el => { el.textContent = message; el.hidden = false; });
    root.querySelectorAll('[data-hf-cookie-retry]').forEach(el => { el.hidden = false; });
  }
  function clearError() {
    root.querySelectorAll('[data-hf-cookie-error]').forEach(el => { el.textContent = ''; el.hidden = true; });
    root.querySelectorAll('[data-hf-cookie-retry]').forEach(el => { el.hidden = true; });
  }
  function busy(value) {
    saving = value;
    root.querySelectorAll('[data-hf-cookie-accept],[data-hf-cookie-reject],[data-hf-cookie-save],[data-hf-cookie-opt-out]').forEach(button => { button.disabled = value; });
    root.setAttribute('aria-busy', String(value));
  }
  function ensureApi() {
    if (typeof api()?.setTrackingConsent === 'function') return Promise.resolve(api());
    if (loading) return loading;
    loading = new Promise((resolve, reject) => {
      let finished = false;
      const timeout = window.setTimeout(() => finish(new Error('Privacy settings are taking longer to load. Please try again.')), 9000);
      function finish(error) {
        if (finished) return;
        finished = true;
        window.clearTimeout(timeout);
        if (error || typeof api()?.setTrackingConsent !== 'function') reject(new Error('Privacy settings could not be loaded. Your choices have not been saved. Please try again.'));
        else resolve(api());
      }
      if (typeof window.Shopify?.loadFeatures !== 'function') return finish(new Error('Shopify is not ready'));
      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], finish);
    }).finally(() => { loading = null; });
    return loading;
  }
  function currentConsent(privacy) { return privacy.currentVisitorConsent(); }
  function hasChoice(consent) { return purposes.some(purpose => consent[purpose] === 'yes' || consent[purpose] === 'no'); }
  function syncControls(privacy) {
    const consent = currentConsent(privacy);
    root.querySelectorAll('[data-hf-cookie-purpose]').forEach(input => { input.checked = consent[input.dataset.hfCookiePurpose] === 'yes'; });
    const sale = root.querySelector('[data-hf-cookie-sale]');
    const optedOut = consent.sale_of_data === 'no';
    const gpc = navigator.globalPrivacyControl === true;
    sale.hidden = !gpc && !optedOut && !(typeof privacy.saleOfDataRegion === 'function' && privacy.saleOfDataRegion());
    const description = sale.querySelector('[data-hf-cookie-sale-description]');
    const button = sale.querySelector('[data-hf-cookie-opt-out]');
    description.textContent = gpc
      ? 'Your browser sends a Global Privacy Control signal. These controls will not override it. Shopify honors this signal in supported opt-out regions.'
      : optedOut ? 'Your opt-out of data sale or sharing is recorded for this browser.'
        : 'You can separately opt out of the sale or sharing of personal information for advertising.';
    button.hidden = optedOut;
    button.textContent = gpc ? 'Confirm data-sharing opt-out' : 'Opt out of data sharing';
  }
  function closeUi() {
    banner.hidden = true;
    if (dialog.open) dialog.close();
    if (returnFocus?.isConnected) returnFocus.focus();
  }
  function showCustomPreferences(trigger) {
    returnFocus = trigger || document.activeElement;
    banner.hidden = true;
    clearError();
    if (!dialog.open) dialog.showModal();
    // Unknown choices remain unchecked. Opening or closing does not write consent.
    ensureApi().then(syncControls).catch(error => showError(error.message));
  }
  async function showPreferences(trigger) {
    const native = nativeBanner();
    if (native) {
      banner.hidden = true;
      try { await native.showPreferences(); return; }
      catch (_) { /* The custom interface uses the same documented privacy API. */ }
    }
    showCustomPreferences(trigger);
  }
  async function record(consent, dataSaleOnly = false) {
    if (saving) return;
    clearError();
    busy(true);
    try {
      const privacy = await ensureApi();
      // Never send sale_of_data:true, including when the visitor chooses Accept all.
      if (navigator.globalPrivacyControl === true && consent.sale_of_data === true) delete consent.sale_of_data;
      await new Promise((resolve, reject) => {
        const timeout = window.setTimeout(() => reject(new Error('We could not confirm that your choices were saved. Please try again.')), 9000);
        privacy.setTrackingConsent(consent, result => {
          window.clearTimeout(timeout);
          if (result?.error) return reject(new Error('Your choices could not be saved. Please try again.'));
          const saved = currentConsent(privacy);
          const matches = Object.entries(consent).every(([key, value]) => saved[key] === (value ? 'yes' : 'no'));
          matches ? resolve() : reject(new Error('We could not confirm that your choices were saved. Please try again.'));
        });
      });
      syncControls(privacy);
      if (!dataSaleOnly) { dismissed = true; closeUi(); }
      announcement.textContent = dataSaleOnly ? 'Your data-sharing opt-out has been saved.' : 'Your cookie preferences have been saved.';
    } catch (error) { showError(error.message || 'Your choices could not be saved. Please try again.'); }
    finally { busy(false); }
  }
  function maybeShowBanner(privacy) {
    if (dismissed || saving || dialog.open || nativeBanner()) return;
    const consent = currentConsent(privacy);
    if (hasChoice(consent)) return;
    const regional = typeof privacy.shouldShowBanner === 'function' && privacy.shouldShowBanner();
    if (regional || root.dataset.globalBanner === 'true') banner.hidden = false;
  }

  document.addEventListener('click', event => {
    const trigger = event.target.closest('[data-hf-cookie-settings]');
    if (!trigger) return;
    event.preventDefault();
    showPreferences(trigger);
  });
  root.addEventListener('click', event => {
    const button = event.target.closest('button');
    if (!button) return;
    if (button.matches('[data-hf-cookie-close]')) { dismissed = true; closeUi(); }
    else if (button.matches('[data-hf-cookie-preferences]')) showPreferences(button);
    else if (button.matches('[data-hf-cookie-accept]')) record({ preferences: true, analytics: true, marketing: true });
    else if (button.matches('[data-hf-cookie-reject]')) record({ preferences: false, analytics: false, marketing: false });
    else if (button.matches('[data-hf-cookie-save]')) {
      const consent = {};
      root.querySelectorAll('[data-hf-cookie-purpose]').forEach(input => { consent[input.dataset.hfCookiePurpose] = input.checked; });
      record(consent);
    } else if (button.matches('[data-hf-cookie-opt-out]')) record({ sale_of_data: false }, true);
    else if (button.matches('[data-hf-cookie-retry]')) {
      clearError();
      ensureApi().then(privacy => { syncControls(privacy); announcement.textContent = 'Privacy settings are ready. Select your choices and save them.'; }).catch(error => showError(error.message));
    }
  });
  dialog.addEventListener('cancel', () => { dismissed = true; });
  dialog.addEventListener('close', () => { if (returnFocus?.isConnected) returnFocus.focus(); });
  document.addEventListener('visitorConsentCollected', () => {
    if (!api()) return;
    if (!saving) syncControls(api());
    if (hasChoice(currentConsent(api()))) banner.hidden = true;
  });

  // Give Shopify's native banner time to initialize. If it appears later, yield
  // to it instead of showing two banners. No consent is changed by this observer.
  const observer = new MutationObserver(() => {
    if (nativeBanner()) {
      banner.hidden = true;
      window.clearTimeout(autoTimer);
      observer.disconnect();
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });
  window.addEventListener('pagehide', () => observer.disconnect(), { once: true });
  ensureApi().then(privacy => {
    syncControls(privacy);
    autoTimer = window.setTimeout(() => maybeShowBanner(privacy), 1200);
  }).catch(() => { /* Footer preferences remain available with a visible retry state. */ });
})();
